import React, { useState } from 'react';
import { Modal, Form, Input, Button, Typography, Select, Upload, Row, Col, message } from 'antd';
import { CloseOutlined, InboxOutlined } from '@ant-design/icons';
import { catchError } from '../../utils/errorHandler';
import axiosInstance from '../../services/axiosInstance';

const { Title } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

const HelpCenterTicketModal = ({ open, onClose, selectedOption, fetchMyTicketListData, fetchAllTicketListData, filters }) => {
    const [form] = Form.useForm();
    const [relatedToPrev, setRelatedToPrev] = useState("no");
    const [uploadFile, setUploadFile] = useState([]);
    const [formSubmitLoading, setFormSubmitLoading] = useState(false);

    const handleSubmit = async () => {
        setFormSubmitLoading(true);
        try {
            const values = await form.validateFields();

            const formData = new FormData();
            formData.append('issue_category', values.issueType);
            formData.append('title', values.title);
            formData.append('description', values.description);
            if (uploadFile.length > 0) {
                uploadFile.forEach(file => {
                    formData.append('issue_files', file.originFileObj);
                });
            }
            if (relatedToPrev === "yes" && values.oldRaiseId) {
                formData.append('reference_ticket', values.oldRaiseId);
            }

            const res = await axiosInstance.post('/useraccount/v2/create-ticket/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (res.status === 201) {
                message.success(res.data.data)
                form.resetFields();
                setUploadFile(null);
                if (selectedOption === "my-ticket") {
                    fetchMyTicketListData();
                } else {
                    fetchAllTicketListData(filters);
                }
                onClose();
            }

            setFormSubmitLoading(false);

        } catch (err) {
            await catchError(err);
            setFormSubmitLoading(false);
        }
    };

    const handleRelatedChange = (value) => {
        setRelatedToPrev(value);
        if (value === "no") {
            form.setFieldsValue({ oldRaiseId: undefined });
        }
    };

    const handleUploadChange = (info) => {
        if (info.fileList.length > 0) {
            setUploadFile(info.fileList || []);
        } else {
            setUploadFile([]);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            styles={{ body: { height: "600px" } }}
            className="create-order"
            width={800}
            maskClosable={false}
            title={
                <div
                    className="custom-modal-header"
                    style={{ display: "flex", justifyContent: "space-between" }}>
                    <Title
                        level={3}
                        style={{
                            color: "white",
                            margin: 0,
                            position: "relative",
                            left: "12px",
                        }}>
                        Ticket Form
                    </Title>
                    <div style={{ marginRight: "10px", display: "flex", gap: "10px", alignItems: "center" }}>
                        <CloseOutlined
                            style={{ fontSize: "16px", cursor: "pointer", color: "white" }}
                            onClick={onClose}
                        />
                    </div>
                </div>
            }
            closable={false}
            footer={[
                <Button
                    key="submit"
                    type="primary"
                    onClick={handleSubmit}
                    style={{ background: "#000" }}
                    loading={formSubmitLoading}
                >
                    Submit
                </Button>
            ]}
            bodyStyle={{ maxHeight: "60vh", overflowY: "auto" }}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Row gutter={16}>
                    {/* Issue Type Dropdown */}
                    <Col xs={24} md={12}>
                        <Form.Item
                            style={{ width: "100%" }}
                            name="issueType"
                            label="Issue Type"
                            rules={[{ required: true, message: 'Please select an issue type' }]}
                        >
                            <Select placeholder="Select Issue Type">
                                <Option value="Access Issue">Access Issue</Option>
                                <Option value="Permissions/Role Issue">Permissions/Role Issue</Option>
                                <Option value="Bug/Error">Bug/Error</Option>
                                <Option value="Request for Edit/Delete Transaction">Request for Edit/Delete Transaction</Option>
                                <Option value="Feature Clarification">Feature Clarification</Option>
                                <Option value="Calling Issue">Calling Issue</Option>
                                <Option value="Others">Others</Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    {/* Title Input */}
                    <Col xs={24} md={12}>
                        <Form.Item
                            style={{ width: "100%" }}
                            name="title"
                            label="Title"
                            rules={[{ required: true, message: 'Please enter a title' }]}
                        >
                            <Input placeholder="Enter title" />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Description TextArea */}
                <Form.Item
                    name="description"
                    label="Detailed Description"
                    rules={[{ required: true, message: 'Please enter a description' }]}
                >
                    <Input.TextArea rows={4} placeholder="Describe your issue..." />
                </Form.Item>

                {/* Related to previous ticket */}
                <Form.Item
                    name="relatedToPrev"
                    label="Related to Previous Ticket?"
                    rules={[{ required: true, message: 'Please select Yes or No' }]}
                >
                    <Select placeholder="Select" onChange={handleRelatedChange}>
                        <Option value="yes">Yes</Option>
                        <Option value="no">No</Option>
                    </Select>
                </Form.Item>

                {/* Old Ticket ID */}
                <Row style={{ width: "100%" }}>
                    {relatedToPrev === "yes" && (
                        <Form.Item
                            style={{ width: "100%" }}
                            name="oldRaiseId"
                            label="Reference to Previous Ticket"
                            rules={[{ required: true, message: 'Please enter the previous ticket ID' }]}
                        >
                            <Input placeholder="Enter previous ticket ID" />
                        </Form.Item>
                    )}
                </Row>

                {/* Attachment */}
                <Form.Item
                    name="attachment"
                    label="Attachment"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => e && e.fileList}
                >
                    <Dragger
                        name="files"
                        multiple={true}
                        maxCount={10}
                        beforeUpload={() => false} // prevent auto upload
                        onChange={handleUploadChange}
                    >
                        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                        <p className="ant-upload-text">Click or drag file to this area to upload</p>
                    </Dragger>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default HelpCenterTicketModal;
